import React, { useEffect, useRef } from 'react';
import { Select, Row, Col, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { DriverFilterOption } from '../../../../../utils/driverFilterOption';
import {
    LEVEL1_ORDER,
    LEVEL2_BY_LEVEL1,
    UndeliveredLevel1,
    UndeliveredLevel2,
    getLevel1Label,
    getLevel2Label,
} from '../undeliveredAttribution';

const { Option } = Select;

/** 司机远程搜索防抖间隔，与 components/SearchSelect 默认值保持一致 */
const DRIVER_SEARCH_DEBOUNCE_MS = 300;

// 详情抽屉多选下拉保持单行高度，多选时横向滚动
const SINGLE_LINE_SELECT_CLASS = 'detail-drawer-select-single-line';

// 详情抽屉状态选项：value 为后端 statusField，与 getStatusColumn 一致
export const DETAIL_STATUS_OPTIONS: { value: string }[] = [
    { value: 'isActualArrivals' },
    { value: 'isPicked' },
    { value: 'isMissScanWhsArrival' },
    { value: 'isUnpicked' },
    { value: 'isUnpicked12h' },
    { value: 'isInDelivery' },
    { value: 'isUndelivered24h' },
    { value: 'isDeliveredLatest' },
    { value: 'isFailedReturnedToWhs' },
    { value: 'isFailedIncomplete' },
    { value: 'isReturnedParcel' },
    { value: 'isLostDamaged' },
    { value: 'isNoUpdate24h' },
    { value: 'isNoUpdate72h' },
    { value: 'isNoUpdate48To72h' },
    { value: 'isNoUpdate48To72hWarehouse' },
    { value: 'isNoUpdate48To72hDriver' },
    { value: 'isNoUpdateOver72h' },
    { value: 'isNoUpdateOver72hWarehouse' },
    { value: 'isNoUpdateOver72hDriver' },
    { value: 'isReturnedParcelIncompleteOver72h' },
    { value: 'isParcelWithReturnStartTime' },
    { value: 'isFailedReturnedToWhsUnprocessedOver24h' },
    { value: 'isParcelWithReturnWarehouseScan' },
    // 未妥投 = 4 个一级归因任一命中，与表格「未妥投总数」同口径
    { value: 'isUndeliveredLc' }
];

export interface DetailFilters {
    selectedDsps: string[];
    selectedAreas: string[];
    selectedDrivers: string[];
    selectedStatuses: string[]; // 后端 statusField 值，多选
    selectedWarehouses?: string[]; // Hub WHS 维度下选中的仓库 ID 列表
    // v2 未妥投归因两级筛选；单选，与 SlaUndeliveredReasonLevel1/2 枚举 name() 对齐
    undeliveredReasonLevel1?: UndeliveredLevel1;
    undeliveredReasonLevel2?: UndeliveredLevel2;
}

interface DetailFilterProps {
    filters: DetailFilters;
    onFiltersChange: (filters: DetailFilters) => void;
    dsps: Array<{ id: number; name: string }>;
    areas: Array<{ id: number; name: string }>;
    drivers: DriverFilterOption[];
    viewMode: 'driver' | 'area';
    noWrapper?: boolean; // 是否不包含外层包装div
    currentDspId?: number; // 当前DSP ID（DSP经理角色）
    dspMode?: boolean; // 是否为DSP经理
    /** Hub 页：'whs' 时第一列显示 WHS 下拉，'dsp' 时显示 DSP 下拉；parcel 页不传则显示 DSP */
    detailDimension?: 'whs' | 'dsp';
    /** Hub 页 WHS 维度下可选的仓库列表（id+name） */
    warehouses?: Array<{ id: number; name: string }>;
    /** 司机远程搜索；传入时司机下拉候选由后端按关键词返回，需关闭本地过滤 */
    onDriverSearch?: (keyword: string) => void;
    driverOptionsLoading?: boolean;
}

const DetailFilter: React.FC<DetailFilterProps> = ({
    filters,
    onFiltersChange,
    dsps,
    areas,
    drivers,
    viewMode,
    noWrapper = false,
    currentDspId,
    dspMode = false,
    detailDimension,
    warehouses = [],
    onDriverSearch,
    driverOptionsLoading
}) => {
    const { t } = useTranslation();

    // 司机为远程搜索时逐字符请求会打满后端，与 components/SearchSelect 一致做 300ms 防抖
    const driverSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (driverSearchTimer.current) clearTimeout(driverSearchTimer.current);
    }, []);
    const handleDriverSearch = onDriverSearch
        ? (keyword: string) => {
            if (driverSearchTimer.current) clearTimeout(driverSearchTimer.current);
            driverSearchTimer.current = setTimeout(() => onDriverSearch(keyword), DRIVER_SEARCH_DEBOUNCE_MS);
        }
        : undefined;
    // antd 多选选中后只清空输入框文本，不会回调 onSearch('')，关键词会一直停在上次搜索词，
    // 导致候选被冻结成「上次搜索的结果」。故在选中/清除与下拉收起时主动把关键词清回空。
    const resetDriverKeyword = () => {
        if (!onDriverSearch) return;
        if (driverSearchTimer.current) clearTimeout(driverSearchTimer.current);
        onDriverSearch('');
    };
    const showWhsFilter = detailDimension === 'whs';
    // 区域前始终显示 DSP：Hub WHS 维度为 WHS → DSP → 区域；Hub DSP 维度为 DSP → 区域
    const showDspFilter = true;

    const singleLineStyle = (
        <style>{`
            .${SINGLE_LINE_SELECT_CLASS}.ant-select .ant-select-selector { height: 32px !important; min-height: 32px !important; overflow-x: auto !important; overflow-y: hidden !important; align-items: center !important; }
            .${SINGLE_LINE_SELECT_CLASS}.ant-select .ant-select-selection-overflow { flex-wrap: nowrap !important; }
        `}</style>
    );
    // 筛选顺序：WHS(仅 Hub whs 维度) → DSP → 区域 → 司机 → 状态（四分平均分）
    const content = (
        <Row gutter={[16, 16]} align="middle">
            {showWhsFilter && (
                <Col span={6}>
                    <Select
                        mode="multiple"
                        value={filters.selectedWarehouses ?? []}
                        onChange={(value) => onFiltersChange({ ...filters, selectedWarehouses: value })}
                        className={`w-full ${SINGLE_LINE_SELECT_CLASS}`}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.whs')}
                        allowClear
                        showSearch
                        maxTagCount={100}
                        filterOption={(input, option) =>
                            String((option as { id?: string; name?: string })?.name ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        fieldNames={{ label: 'name', value: 'id' }}
                        options={warehouses.map((w) => ({ id: String(w.id), name: w.name }))}
                    />
                </Col>
            )}
            {showDspFilter && (
                <Col span={6}>
                    <Select
                        mode="multiple"
                        value={filters.selectedDsps}
                        onChange={(value) => onFiltersChange({ ...filters, selectedDsps: value })}
                        className={`w-full ${SINGLE_LINE_SELECT_CLASS}`}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.dsp')}
                        allowClear={!dspMode}
                        showSearch
                        maxTagCount={100}
                        filterOption={(input, option) =>
                            String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={dspMode}
                    >
                        {dsps.map((dsp) => (
                            <Option key={dsp.id} value={dsp.name}>{dsp.name}</Option>
                        ))}
                    </Select>
                </Col>
            )}
            <Col span={6}>
                <Select
                    mode="multiple"
                    value={filters.selectedAreas}
                    onChange={(value) => onFiltersChange({...filters, selectedAreas: value})}
                    className={`w-full ${SINGLE_LINE_SELECT_CLASS}`}
                    placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.area')}
                    allowClear
                    showSearch
                    maxTagCount={100}
                    filterOption={(input, option) =>
                        String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {areas.map((area) => (
                        <Option key={area.id} value={area.name}>{area.name}</Option>
                    ))}
                </Select>
            </Col>
            <Col span={6}>
                <Select
                    mode="multiple"
                    value={filters.selectedDrivers}
                    onChange={(value) => {
                        resetDriverKeyword();
                        onFiltersChange({...filters, selectedDrivers: value});
                    }}
                    className={`w-full ${SINGLE_LINE_SELECT_CLASS}`}
                    placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.driver')}
                    allowClear
                    showSearch
                    maxTagCount={100}
                    onSearch={handleDriverSearch}
                    onDropdownVisibleChange={handleDriverSearch ? (open) => { if (!open) resetDriverKeyword(); } : undefined}
                    loading={driverOptionsLoading}
                    filterOption={
                        handleDriverSearch
                            ? false
                            : (input, option) =>
                                String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {drivers.map(driver => (
                        <Option key={driver.accountId} value={String(driver.accountId)} title={driver.title}>{driver.name}</Option>
                    ))}
                </Select>
            </Col>
            <Col span={6}>
                <Select
                    mode="multiple"
                    value={filters.selectedStatuses}
                    onChange={(value) => onFiltersChange({ ...filters, selectedStatuses: value })}
                    className={`w-full ${SINGLE_LINE_SELECT_CLASS}`}
                    placeholder={t('wms.delivery.parcelSlaMonitoring.detailDrawer.statusFilter')}
                    allowClear
                    showSearch
                    maxTagCount={100}
                    filterOption={(input, option) =>
                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={DETAIL_STATUS_OPTIONS.map((opt) => ({
                        value: opt.value,
                        label: t(`wms.delivery.parcelSlaMonitoring.detailStatusOptions.${opt.value}`)
                    }))}
                />
            </Col>
            {/* 未妥投归因两级筛选：一级变化时清空二级，避免出现「一级=司机侧、二级=仓库侧子项」的非法组合 */}
            <Col span={6}>
                <Select
                    value={filters.undeliveredReasonLevel1}
                    onChange={(value) => onFiltersChange({
                        ...filters,
                        undeliveredReasonLevel1: value as UndeliveredLevel1 | undefined,
                        undeliveredReasonLevel2: undefined,
                    })}
                    className="w-full"
                    placeholder={t('wms.delivery.parcelSlaMonitoring.detailFilter.reasonLevel1Placeholder')}
                    allowClear
                    options={LEVEL1_ORDER.map((l1) => ({ value: l1, label: getLevel1Label(t, l1) }))}
                />
            </Col>
            <Col span={6}>
                <Select
                    value={filters.undeliveredReasonLevel2}
                    onChange={(value) => onFiltersChange({
                        ...filters,
                        undeliveredReasonLevel2: value as UndeliveredLevel2 | undefined,
                    })}
                    className="w-full"
                    placeholder={t('wms.delivery.parcelSlaMonitoring.detailFilter.reasonLevel2Placeholder')}
                    allowClear
                    disabled={!filters.undeliveredReasonLevel1}
                    options={(filters.undeliveredReasonLevel1
                        ? LEVEL2_BY_LEVEL1[filters.undeliveredReasonLevel1]
                        : []
                    ).map((l2) => ({ value: l2, label: getLevel2Label(t, l2) }))}
                />
            </Col>
        </Row>
    );
    
    if (noWrapper) {
        return (
            <>
                {singleLineStyle}
                {content}
            </>
        );
    }

    return (
        <div className="mb-4 pb-4 border-b">
            {singleLineStyle}
            {content}
        </div>
    );
};

export default DetailFilter;
